import { supabase } from '@/lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

type SubscriptionCallback = (payload: any) => void;

interface ChannelInfo {
  channel: RealtimeChannel;
  subscriptions: Set<string>;
}

class RealtimeSubscriptionManager {
  private channels: Map<string, ChannelInfo> = new Map();
  private callbacks: Map<string, SubscriptionCallback> = new Map();

  /**
   * 상품 재고 변경을 구독합니다.
   * @param productIds 구독할 상품 ID 배열
   * @param callback 재고 변경 시 호출될 콜백
   * @param subscriberId 구독자 고유 ID (컴포넌트 식별용)
   * @returns 구독 해제 함수
   */
  subscribeToProductStock(
    productIds: string[],
    callback: SubscriptionCallback,
    subscriberId: string
  ): () => void {
    const channelKey = `product-stock-${productIds.sort().join('-')}`;
    const callbackKey = `${channelKey}-${subscriberId}`;

    // 콜백 저장
    this.callbacks.set(callbackKey, callback);

    // 기존 채널이 있으면 재사용
    if (this.channels.has(channelKey)) {
      const channelInfo = this.channels.get(channelKey)!;
      channelInfo.subscriptions.add(callbackKey);
      return () => this.unsubscribe(channelKey, callbackKey);
    }

    // 새 채널 생성
    const channel = supabase
      .channel(channelKey)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_stock',
          filter: `product_id=in.(${productIds.join(',')})`
        },
        (payload) => {
          // 모든 구독자에게 알림
          this.channels.get(channelKey)?.subscriptions.forEach(cbKey => {
            this.callbacks.get(cbKey)?.(payload);
          });
        }
      )
      .subscribe();

    this.channels.set(channelKey, {
      channel,
      subscriptions: new Set([callbackKey])
    });

    return () => this.unsubscribe(channelKey, callbackKey);
  }

  /**
   * 매장의 모든 상품 재고 변경을 구독합니다.
   * @param restaurantId 매장 ID
   * @param callback 재고 변경 시 호출될 콜백
   * @param subscriberId 구독자 고유 ID
   * @returns 구독 해제 함수
   */
  subscribeToRestaurantStock(
    restaurantId: string,
    callback: SubscriptionCallback,
    subscriberId: string
  ): () => void {
    const channelKey = `restaurant-stock-${restaurantId}`;
    const callbackKey = `${channelKey}-${subscriberId}`;

    this.callbacks.set(callbackKey, callback);

    if (this.channels.has(channelKey)) {
      const channelInfo = this.channels.get(channelKey)!;
      channelInfo.subscriptions.add(callbackKey);
      return () => this.unsubscribe(channelKey, callbackKey);
    }

    const channel = supabase
      .channel(channelKey)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_stock',
          filter: `product_id=in.(select id from products where restaurant_id=eq.${restaurantId})`
        },
        (payload) => {
          this.channels.get(channelKey)?.subscriptions.forEach(cbKey => {
            this.callbacks.get(cbKey)?.(payload);
          });
        }
      )
      .subscribe();

    this.channels.set(channelKey, {
      channel,
      subscriptions: new Set([callbackKey])
    });

    return () => this.unsubscribe(channelKey, callbackKey);
  }

  /**
   * 구독을 해제합니다.
   */
  private unsubscribe(channelKey: string, callbackKey: string) {
    const channelInfo = this.channels.get(channelKey);
    if (!channelInfo) return;

    // 콜백 제거
    this.callbacks.delete(callbackKey);
    channelInfo.subscriptions.delete(callbackKey);

    // 더 이상 구독자가 없으면 채널 제거
    if (channelInfo.subscriptions.size === 0) {
      supabase.removeChannel(channelInfo.channel);
      this.channels.delete(channelKey);
    }
  }


  /**
   * 모든 구독을 정리합니다.
   */
  cleanup() {
    this.channels.forEach(({ channel }) => {
      supabase.removeChannel(channel);
    });
    this.channels.clear();
    this.callbacks.clear();
  }
}

// 싱글톤 인스턴스
export const realtimeManager = new RealtimeSubscriptionManager();